// Styling comes from the employees page - to be refactored to tailwind in both places
import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import type { AssetLocation, AssetLocationDetails } from '@/resources/asset-locations'
import assetLocationResources from '@/resources/asset-locations'
import { commonQueries } from '@/resources/queries'

import { ExportToExcel } from '../components/ExportToExcel'

import { AssetLocationListItem } from './AssetLocationListItem'

export const AssetLocationList: FC<{
  searchFilter: string
  locations: AssetLocation[]
}> = ({ searchFilter, locations }) => {
  const translate = useTranslation()

  const [, { handleSort }] =
    useSortParams<keyof (AssetLocationDetails & { departmentName: string })>()

  const { isLoading, isError } = useQuery(
    commonQueries.assetLocationList(),
    assetLocationResources.getList
  )

  const { visibleList, ListLoaderComponent } = useRenderLimit(locations || [], 30)

  if (isLoading) {
    return <LoaderCircle />
  }

  if (isError || locations.length === 0) {
    return <NoResults />
  }

  const excelExportData = locations.map(item => ({
    id: item.id,
    code: item.code,
    email: item.email,
    hidden: item.hidden ? translate('yes') : translate('no'),
    department: `${item.department.code} - ${item.department.name}`,
    name: item.name,
    accountingCode: item.accountingCode,
    description: item.description,
    customer: item.customer.name,
    customerAddress: item.customerAddress?.address,
  }))

  return (
    <>
      <ExportToExcel data={excelExportData} filename={translate('locations')} />
      <Table rowHeight="tall">
        <thead>
          <tr>
            <Th id="name" onClick={handleSort}>
              <Translation id="Location" />
            </Th>

            <Th id="description" onClick={handleSort}>
              <Translation id="Name" />
            </Th>

            <Th id="departmentName" className="hidden md:table-cell" onClick={handleSort}>
              <Translation id="Department" />
            </Th>

            <Th id="email" className="hidden md:table-cell" onClick={handleSort}>
              <Translation id="Email" />
            </Th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <AssetLocationListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </tbody>
      </Table>

      <ListLoaderComponent />
    </>
  )
}
